// Part Intelligence HUMAN decisions: pure model, no database.
//
// Read docs/part-intelligence/FEATURE_CHARTER.md before changing anything here.
//
// A human decision is a third, separate fact about an identifier. It never
// replaces the machine evidence or the machine assessment, and it never
// rewrites the customer's identifier (rfq_line_items.part_number):
//
//   machine evidence     what FLIS / PartsBase returned     (evidence.ts)
//   machine assessment   corroborated / possible / unknown  (evidence.ts)
//   human decision       what a broker recorded, who, when  (this file)
//
// Decisions are tenant-private operational knowledge. Nothing here promotes a
// broker's confirmation to shared reference data.
//
// There is deliberately NO "invalid" decision. The charter reserves invalidity
// for a person, and Phase 1B chose not to offer it at all: a wrong "invalid"
// costs far more than any of the decisions below, and deserves its own
// deliberate workflow. Unknown remains the safe resting state.

import { z } from 'zod';
import type { MachineAssessment, SourceSearchResult } from './evidence';

/** Three independent kinds. A line can carry one current value of each: a
 *  broker can confirm the identity AND flag that the catalog page was wrong. */
export const DECISION_KINDS = ['identity', 'catalog_issue', 'identifier_type'] as const;
export type DecisionKind = (typeof DECISION_KINDS)[number];

/** `cleared` withdraws the current value of a kind without erasing history. */
export const IDENTITY_VALUES = ['confirmed_match', 'needs_research', 'cleared'] as const;
export const CATALOG_ISSUE_VALUES = ['suspected', 'cleared'] as const;
/** The charter's identifier categories. A human label only; never inferred. */
export const IDENTIFIER_TYPE_VALUES = [
  'aircraft_part',
  'nsn',
  'tooling',
  'material_chemical_consumable',
  'specification_drawing',
  'unknown',
  'cleared',
] as const;

export const VALUES_BY_KIND: Record<DecisionKind, readonly string[]> = {
  identity: IDENTITY_VALUES,
  catalog_issue: CATALOG_ISSUE_VALUES,
  identifier_type: IDENTIFIER_TYPE_VALUES,
};

/** Where a confirmed candidate came from. Recorded so a later benchmark can
 *  tell "the broker confirmed what the software proposed" from "the broker
 *  found it elsewhere".
 *
 *  as_entered      the identifier is right as the customer entered it
 *                  (candidate = the conservative lookup key)
 *  flis_candidate  one of the FLIS reference numbers in the evidence; the
 *                  server checks it is actually there
 *  broker_entered  found by the broker's own research */
export const CANDIDATE_SOURCES = ['as_entered', 'flis_candidate', 'broker_entered'] as const;
export type CandidateSource = (typeof CANDIDATE_SOURCES)[number];

export const MAX_NOTE = 1000;
export const MAX_CANDIDATE = 100;

/** Request body for recording one decision. */
export const DecisionInput = z
  .object({
    kind: z.enum(DECISION_KINDS),
    value: z.string().min(1).max(64),
    candidateIdentifier: z.string().trim().min(1).max(MAX_CANDIDATE).optional(),
    candidateSource: z.enum(CANDIDATE_SOURCES).optional(),
    note: z.string().trim().max(MAX_NOTE).optional(),
  })
  .superRefine((d, ctx) => {
    if (!VALUES_BY_KIND[d.kind].includes(d.value)) {
      ctx.addIssue({ code: 'custom', path: ['value'], message: `"${d.value}" is not a valid ${d.kind} decision` });
    }
    const confirming = d.kind === 'identity' && d.value === 'confirmed_match';
    if (confirming && (!d.candidateIdentifier || !d.candidateSource)) {
      ctx.addIssue({ code: 'custom', path: ['candidateIdentifier'], message: 'Confirm match needs the matched identifier and where it came from' });
    }
    if (!confirming && (d.candidateIdentifier || d.candidateSource)) {
      ctx.addIssue({ code: 'custom', path: ['candidateIdentifier'], message: 'Only Confirm match carries a candidate identifier' });
    }
  });
export type DecisionInput = z.infer<typeof DecisionInput>;

/** One stored decision, as the API returns it. Public ids only. */
export interface DecisionRecord {
  id: string;
  kind: DecisionKind;
  value: string;
  candidateIdentifier: string | null;
  candidateSource: CandidateSource | null;
  note: string | null;
  /** The line's identifier when the decision was made (the line can be edited). */
  originalIdentifier: string;
  /** What the software said at that moment. */
  machineStateAtDecision: MachineAssessment['state'] | null;
  catalogSourcePath: string | null;
  decidedBy: string | null;
  decidedAt: string;
  /** Public id of the decision this one replaced, if any. */
  supersedes: string | null;
}

export interface CurrentDecisions {
  identity: DecisionRecord | null;
  catalogIssue: DecisionRecord | null;
  identifierType: DecisionRecord | null;
}

/** The current value of each kind: the newest record, unless it is `cleared`. */
export function currentDecisions(history: DecisionRecord[]): CurrentDecisions {
  const newest = (kind: DecisionKind) => {
    let best: DecisionRecord | null = null;
    for (const r of history) if (r.kind === kind && (!best || r.decidedAt > best.decidedAt)) best = r;
    return best && best.value !== 'cleared' ? best : null;
  };
  return {
    identity: newest('identity'),
    catalogIssue: newest('catalog_issue'),
    identifierType: newest('identifier_type'),
  };
}

/** "What should the broker do next?" Guidance only; changes nothing. */
export function suggestNextAction(input: {
  machine: Pick<MachineAssessment, 'state' | 'needsReview'>;
  partsbase: Pick<SourceSearchResult, 'state'>;
  decisions: CurrentDecisions;
}): string {
  const { machine, partsbase, decisions } = input;
  if (decisions.identity?.value === 'confirmed_match') {
    return 'Identity confirmed by a broker. Source against the confirmed identifier.';
  }
  if (decisions.identity?.value === 'needs_research') {
    return 'Marked for research. Unknown is a valid state; the RFQ can still be worked.';
  }
  if (machine.state === 'possible_match') {
    return 'Review the possible match. Confirm it if it is the same part, or mark Needs research.';
  }
  if (partsbase.state === 'error') {
    return 'The last PartsBase search failed. Search again from PartsBase listings when ready.';
  }
  if (machine.state === 'corroborated') {
    return 'An independent source lists this exact identifier. Source it; confirm the match if you have verified it.';
  }
  if (partsbase.state === 'not_searched') {
    return 'No positive evidence yet. PartsBase has not been searched for this identifier.';
  }
  return 'No source lists this identifier. That does not make it wrong; mark Needs research if you cannot identify it.';
}

export const MACHINE_STATE_LABEL: Record<MachineAssessment['state'], string> = {
  corroborated: 'Corroborated',
  possible_match: 'Possible match',
  unknown: 'Unknown',
};

export const SEARCH_STATE_LABEL = {
  not_searched: 'Not searched',
  hit: 'Hit',
  no_hit: 'No hit',
  error: 'Error',
} as const;

export const IDENTITY_LABEL: Record<(typeof IDENTITY_VALUES)[number], string> = {
  confirmed_match: 'Confirmed match',
  needs_research: 'Needs research',
  cleared: 'Cleared',
};

export const IDENTIFIER_TYPE_LABEL: Record<(typeof IDENTIFIER_TYPE_VALUES)[number], string> = {
  aircraft_part: 'Aircraft part',
  nsn: 'NSN',
  tooling: 'Tooling',
  material_chemical_consumable: 'Material / chemical / consumable',
  specification_drawing: 'Specification / drawing',
  unknown: 'Unknown',
  cleared: 'Cleared',
};

/** The catalog page an RFQ came from, from the intake's raw snapshot. Server
 *  derived only: the browser never supplies it. Null when not a catalog page. */
export function catalogSourcePathFromRaw(sourceRawText: string | null | undefined): string | null {
  if (!sourceRawText) return null;
  try {
    const parsed = JSON.parse(sourceRawText) as { sourcePagePath?: unknown };
    const p = parsed?.sourcePagePath;
    return typeof p === 'string' && /^\/parts\/[A-Za-z0-9._~%-]+$/.test(p) ? p : null;
  } catch {
    return null;
  }
}
